import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { MAT_DATE_LOCALE, MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { NgxMatDatetimePickerModule,  NgxMatNativeDateModule } from '@angular-material-components/datetime-picker';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { createTranslateLoader, formatLanguage } from 'src/utils/translate';
import { AdminGuard } from '../services/admin.guard';
// eslint-disable-next-line max-len
import { AnnouncementDisableModalComponent } from './announcement-management/announcement-disable-modal/announcement-disable-modal.component';
import { AnnouncementManagementComponent } from './announcement-management/announcement-management.component';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthGuard } from '../shared-components/ng-login/auth/auth-guard.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { ChangeRolesComponent } from './user/user-management/change-roles/change-roles.component';
import { ChipInputComponent } from '../shared-components/chip-input/chip-input.component';
import { ConfirmComponent } from './confirm/confirm.component';
import { CreateGrantComponent } from './grant-management/create-grant/create-grant.component';
// eslint-disable-next-line max-len
import { CreateModifyAnnouncementModalComponent } from './announcement-management/create-modify-announcement-modal/create-modify-announcement-modal.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DatePipe } from '@angular/common';
import { DynamicHttpTableModule } from '@components/ng-dynamic-http-table';
import { FormsModule } from '@angular/forms';
import { GrantManagementComponent } from './grant-management/grant-management.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HelpCenterComponent } from './help-center/help-center.component';
import { HttpClient } from '@angular/common/http';
import { ImpersonateModalComponent } from './dashboard/impersonate-modal/impersonate-modal.component';
import { InfoShadowBoxComponent } from './info-shadow-box/info-shadow-box.component';
import { InsightsComponent } from './insights/insights.component';
import { InviteUserComponent } from './user/user-management/invite-user/invite-user.component';
import { LoadingButtonDirective } from '../directives/loading-button.directive';
import { LoadingIconComponent } from '../shared-components/loading-icon/loading-icon.component';
import { LoginComponent } from './login/login.component';
import { LoginModule } from '../shared-components/ng-login/login.module';
import { MainComponent } from './main/main.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationComponent } from '../shared-components/navigation/navigation.component';
import { NavigationItemComponent } from '../shared-components/navigation/navigation-item/navigation-item.component';
import { PasswordStrengthBarComponent } from './password-strength-bar/password-strength-bar.component';
import { ReactivateUserComponent } from './user/user-management/reactivate-user/reactivate-user.component';
import { ReactiveFormsModule } from '@angular/forms';
import { RefererInterceptor } from '../services/referer.interceptor';
import { ReportEmbedComponent } from './report-embed/report-embed.component';
import { ResetPasswordComponent } from './user/reset-password/reset-password.component';
import { RoleGuard } from '../shared-components/ng-login/auth/auth-role.service';
import { ShowHidePasswordComponent } from './show-hide-password/show-hide-password.component';
import { SupportGuard } from '../services/support.guard';
import { UserActivateAccountComponent } from './user/user-activate-account/user-activate-account.component';
import { UserForgotPasswordComponent } from './user/user-forgot-password/user-forgot-password.component';
import { UserManagementComponent } from './user/user-management/user-management.component';
import { UserToImpersonateDataModalComponent }
  from './user/user-management/user-to-impersonate-data-modal/user-to-impersonate-data-modal.component';
import { VerticalSelectorComponent } from './vertical-selector/vertical-selector.component';
import { WelcomeVideoManagementComponent } from './welcome-video-management/welcome-video-management.component';

export const globalImports = [
  BrowserModule,
  AppRoutingModule,
  FormsModule,
  LoginModule,
  BrowserAnimationsModule,
  MatDividerModule,
  MatIconModule,
  MatFormFieldModule,
  MatInputModule,
  MatButtonModule,
  MatCheckboxModule,
  MatCardModule,
  MatChipsModule,
  MatDialogModule,
  ReactiveFormsModule,
  MatSelectModule,
  MatOptionModule,
  MatSlideToggleModule,
  MatSidenavModule,
  MatMenuModule,
  MatTableModule,
  MatToolbarModule,
  MatTooltipModule,
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useFactory: createTranslateLoader,
      deps: [HttpClient]
    },
    defaultLanguage: formatLanguage(navigator.language)
  }),
  DynamicHttpTableModule,
  MatExpansionModule,
  MatProgressSpinnerModule,
  MatDatepickerModule,
  MatNativeDateModule,
  NgxMatDatetimePickerModule,
  NgxMatNativeDateModule
];

function initializeApp(translateService: TranslateService): void {
  const userLanguage = 'es';
  translateService.setDefaultLang(userLanguage);
}
@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    MainComponent,
    ShowHidePasswordComponent,
    NavigationComponent,
    ConfirmComponent,
    NavigationItemComponent,
    PasswordStrengthBarComponent,
    UserForgotPasswordComponent,
    UserActivateAccountComponent,
    ResetPasswordComponent,
    InfoShadowBoxComponent,
    HelpCenterComponent,
    DashboardComponent,
    UserManagementComponent,
    InviteUserComponent,
    DashboardComponent,
    ReportEmbedComponent,
    InsightsComponent,
    LoadingIconComponent,
    ReactivateUserComponent,
    GrantManagementComponent,
    CreateGrantComponent,
    ImpersonateModalComponent,
    CreateGrantComponent,
    VerticalSelectorComponent,
    UserToImpersonateDataModalComponent,
    AnnouncementManagementComponent,
    AnnouncementDisableModalComponent,
    LoadingButtonDirective,
    CreateModifyAnnouncementModalComponent,
    ChangeRolesComponent,
    ChipInputComponent,
    WelcomeVideoManagementComponent
  ],
  imports: [...globalImports, MatTabsModule, MatChipsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    AuthGuard,
    RoleGuard,
    AdminGuard,
    SupportGuard,
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: (translateService: TranslateService) => () => initializeApp(translateService),
      deps: [TranslateService],
      multi: true
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_DATE_LOCALE, useValue: 'es' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefererInterceptor,
      multi: true
    }
  ],
  exports: [PasswordStrengthBarComponent, TranslateModule],
  bootstrap: [AppComponent]
})
export class AppModule {}
